import type { MessageDescriptor } from 'react-intl';

import { token } from '@atlaskit/tokens';

import DiscoveryFeedbackIcon from './images/DiscoveryFeedbackIcon.svg';
import DiscoveryFeedbackPreviewIcon from './images/DiscoveryFeedbackPreviewIcon.svg';
import ProjectPlannerIcon from './images/ProjectPlannerIcon.svg';
import ProjectPlannerPreviewIcon from './images/ProjectPlannerPreviewIcon.svg';
import { i18n } from './messages.ts';

/**
 * Static configuration for the "Rovo Agents for you" cards.
 *
 * PR4 (CCCEMAU-3070): the `agentExternalConfigReference` field is now wired up —
 * clicking "Try it" publishes a `chat-new` event that opens the referenced OOTB
 * agent in the Rovo sidebar (see AgentCard.tsx). We reference the OOTB agent by
 * its external config reference rather than an agent id, since the underlying
 * agent id can change while the OOTB reference is stable.
 *
 * Agent `icon` (top-left badge) and `previewIcon` (preview caption glyph)
 * assets are product SVGs exported from Figma (colours baked in) and rendered
 * as `<img>` sources. `accentColor` is the pastel colour of the stacked-paper
 * "backing sheet" that peeks out behind the white preview sheet.
 */
export type AgentCardConfig = {
	/** Stable key used for React keys, test ids and (later) dismiss persistence. */
	key: string;
	/**
	 * External config reference for the OOTB agent (the stable identifier used to
	 * launch it). Preferred over a raw agent id because the agent id can change
	 * while the OOTB reference is stable.
	 */
	agentExternalConfigReference: string;
	/** URL to the agent's top-left badge SVG icon asset (colours baked in). */
	icon: string;
	/** URL to the preview-caption glyph SVG (sparkle / target / lightbulb). */
	previewIcon: string;
	/** Pastel accent colour token for the preview backing sheet. */
	accentColor: string;
	/**
	 * Exact rotation (degrees) of the white preview sheet, matching the Figma
	 * illustration. Each card tilts a slightly different amount/direction, so the
	 * stacked-paper look is unique per card (e.g. Planner -1.8deg, Discovery &
	 * Feedback +1.8deg).
	 */
	previewTilt: number;
	title: MessageDescriptor;
	description: MessageDescriptor;
	previewCaption: MessageDescriptor;
};

export const AGENT_CARDS: AgentCardConfig[] = [
	{
		key: 'project-planner',
		agentExternalConfigReference: 'planner_agent',
		icon: ProjectPlannerIcon,
		previewIcon: ProjectPlannerPreviewIcon,
		accentColor: token('color.background.discovery'),
		previewTilt: -1.8,
		title: i18n.projectPlannerTitle,
		description: i18n.projectPlannerDescription,
		previewCaption: i18n.projectPlannerPreviewCaption,
	},
	{
		key: 'discovery-feedback',
		agentExternalConfigReference: 'discovery_and_feedback_agent',
		icon: DiscoveryFeedbackIcon,
		previewIcon: DiscoveryFeedbackPreviewIcon,
		accentColor: token('color.background.success'),
		previewTilt: 1.8,
		title: i18n.discoveryFeedbackTitle,
		description: i18n.discoveryFeedbackDescription,
		previewCaption: i18n.discoveryFeedbackPreviewCaption,
	},
];
